import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHeaderHeight } from '@react-navigation/stack';
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View
} from 'react-native';
import theme from '../../constants/theme';
import { Organization, Question, Report, ReportSection, UserRole } from '../../utils/classes';
import { SCREEN_HEIGHT, SCREEN_WIDTH, QUESTIONNAIRE_SHORT_ZOOM } from '../../utils/screensize';
import useDimensions from '../../hooks/useDimensions';
import Button from '../global/Button';
import ConfirmModal, { ModalConfig } from '../global/ConfirmModal';
import LoadingUpload from './LoadingUpload';
import ModalShowBottom from './ModalShowBottom';
import QuestionnaireProgress from './QuestionnaireProgress';
import QuestionnaireSwiper from './QuestionnaireSwiper';
import QuestionnaireUploadCVModal from './QuestionnaireUploadCVModal';
import WarningModal from './WarningModal';

export type QuestionnaireContainerProps = {
  /** Whether is modal shown or not */
  showModal: boolean;

  /** Whether the phone has notch (on the top of the screen) or not */
  hasNotch: boolean;

  /** True if the questionnaire is busy checking with the Computer Vision API */
  isUploadingCV: boolean;

  /** Used for async function. If it's true then system can't be disturbed and async function is possibly running */
  isBusy: boolean;

  /** Check if questionnaire intended for multisite */
  isMultiSite: boolean;

  /**  */
  isTransitioned: boolean;

  /** List of questions in questionnaire that has been completed */
  completedCount: number;

  /** Index of a selected photo that is being edited */
  editingPhotoIndex: number;

  /** Currently used language (en / in) */
  language: string;

  /** Current configuration of a modal shown */
  modalConfig: ModalConfig;

  /** Report data that is currently being selected */
  selectedReport: Report | ReportSection;

  userRole: UserRole | null | undefined;

  /** Organization data that is tied to the report and user */
  organization: Organization | null | undefined;

  /** Currently selected question that is being shown */
  currentQuestionIndex: number;

  /** Get - profile color of the organization */
  profileColor: string;

  /** True if current organization have access to video feature */

  /** Get - is in tutorial mode or not */
  isTutorialMode: boolean;

  /** get - state is show tooltip or not */
  isShowTooltipReview: boolean;

  /** Get - state if NA question is show or not*/
  isShowTooltipNA: boolean;

  /** state for showing tutorial modal on question */
  isShowQuestionTutorialModal: boolean;

  /** active question type , for showing modal based on question type */
  tutorialQuestionType: string;
  isUploading: boolean;

  /** Play a video when activated */
  handleNavPlaybackVideo: (uri: string, videoIndex: number, currentConditionIndex?: number | string) => void;

  /** Save edited photo */
  handleConfirmEditPhoto: (photoIndex: number, skuKey?: string, currentConditionIndex?: number | string) => void;

  /** Show the confirmation modal to clear the current question's answer, comment, and attachments */
  handleConfirmClear: () => void;

  /** Close the questionnaire modal */
  handleCloseQuestionnaireModal: () => void;

  /** Submit questionnaire. When internet is inactive, will be moved to async storage when internet is inactive */
  handleSubmitQuestionnaire: () => void;

  /** Increase current question index by 1, thus change the view to the next question in the questionnaire pool */
  handleMoveToNextQuestion: () => void;

  /** Decrease current question index by 1, thus change the view to the previous question in the questionnaire pool */
  handleMoveToPrevQuestion: () => void;

  /** Push a new page */
  handleNavReference: (url: string) => void;

  /** Open record video page */
  handleNavRecordVideo: (currentConditionIndex?: number | string, props?: any) => void;

  /** Navigate to Create Company page */
  handleNavCreateCompany: () => void;

  /** Save the questionnaire. Debounce prevents this function to be called repeatedly. This function can be called again in a moment after the previous requests is complete / error */
  saveQuestionnaireDebounced: () => void;

  /** Go through each question to ensure the photos are saved in the proper array order and save it into the redux */
  handleFinishSection: (isTriggered?: boolean) => void;

  /** Opens image picker for the current question */
  handleActivateImagePicker: (currentConditionIndex?: number | string, props?: any) => void;

  /** Set recommendation selected by the user */
  handleSetRemedies: (index: number, text: string, currentConditionIndex?: number | string) => void;

  /** Save questionnaire input field to redux state */
  handleInput: (
    name: string,
    index: number,
    input: any,
    condition?: number | string,
    type?: any,
    question?: Question,
    skuKey?: string
  ) => void;

  /** Show or hide tutorial modal on question */
  handleShowTutorialQuestionModal: (isShow: boolean) => void;

  /** Set question type of tutorial */
  handleChangeTutorialQuestionType: (type: string) => void;

  /** Handle dismis of showing tooltip */
  handleDismissTooltipReview: () => void;

  /** Handle close or open tooltip */
  handleShowTooltipNA: (value: boolean) => void;

  /** close tutorial modal on question */
  handleDismissTutorialQuestionModal: () => void;

  /** end tutorial mode */
  handleDismissTutorialMode: () => void;

  handleUploadDocuments: (currentConditionIndex?: number | string, props?: any) => void;

  handleEditUploadFile: (fileIndex: number, currentConditionIndex?: number | string) => void;

  showCamera: boolean;

  handleTakePhoto: (currentConditionIndex?: number | string, props?: any) => void;

  handleCloseCamera: () => void;
};

export const determineAllowGallery = (
  currentQuestion: Question,
  userRole?: UserRole | null | undefined,
  organization?: Organization | null | undefined
): boolean => {
  const isCameraOnly = userRole?.resourceMap['application:photo-source:live-camera']?.create;
  const isCameraAndGallery = userRole?.resourceMap['application:photo-source:allow-gallery']?.create;
  const userRoleSettings = isCameraAndGallery ?? !isCameraOnly ?? undefined;

  const defaultGalleryPermission = !!(currentQuestion.allowGallery ?? userRoleSettings ?? organization?.allowGallery);
  return organization?.allowGallery ? defaultGalleryPermission : false;
};

export const QuestionnaireContainer = (props: QuestionnaireContainerProps) => {
  const { t } = useTranslation(['questionnaire', 'common']);
  // Bound the questionnaire body to the viewport (window height minus the Stack
  // header) so the footer (Clear / Previous / Next) stays pinned and visible even on
  // short screens like 1024x768. Without an explicit bound the page grows past the
  // viewport and the footer drops below the fold — the reason users had to zoom out
  // (Ctrl+-) to reach "Next". (useHeaderHeight is mocked in unit tests via setupTests.)
  const headerHeight = useHeaderHeight();
  const { height: windowHeight } = useDimensions('window');
  // The body container is locally zoomed-out on short viewports (QUESTIONNAIRE_SHORT_ZOOM)
  // while the Stack header is NOT (it lives outside this container). CSS `zoom` scales
  // layout too, so dividing the post-header height by the zoom makes the container
  // occupy exactly `windowHeight - headerHeight` after scaling — footer flush at the
  // bottom, attachment row fully reachable, and no white gap. On normal/phone viewports
  // the zoom is 1, so this is just `windowHeight - headerHeight` (unchanged).
  const availableHeight = Math.max((windowHeight - headerHeight) / QUESTIONNAIRE_SHORT_ZOOM, 0);
  if (!props.isTransitioned) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator color={theme.colors.primary} size="large" />
        <Text>{t('questionnaire:container.loadingQuestionnaire')}</Text>
      </View>
    );
  }

  const { selectedReport, userRole, organization, currentQuestionIndex } = props;
  const currentQuestion = selectedReport?.questions[currentQuestionIndex];
  const allowGallery = determineAllowGallery(currentQuestion, userRole, organization);

  // The right-hand footer button stays "Next" until the auditor reaches the LAST question with every
  // question answered — only then does it switch to the submit action ("Review Report" single-site /
  // "Finish Section" multi-site). This keeps "Next" available on earlier questions even at 100%.
  const isQuestionnaireComplete = props.completedCount >= props.selectedReport.questions.length;
  const isLastQuestion = props.currentQuestionIndex >= props.selectedReport.questions.length - 1;
  const showReviewButton = isQuestionnaireComplete && isLastQuestion;

  return (
    <View style={[styles.rootQusionareContainer, { height: availableHeight, zoom: QUESTIONNAIRE_SHORT_ZOOM } as any]}>
      <ConfirmModal
        isVisible={props.showModal}
        language={props.language}
        title={props.modalConfig.title}
        subtitle={props.modalConfig.subtitle}
        options={props.modalConfig.options}
        onCloseModal={props.handleCloseQuestionnaireModal}
      />

      {props.isUploading && <LoadingUpload />}

      <ModalShowBottom
        showFooterModal={props.showFooterModal}
        allowGallery={allowGallery}
        setShowFooter={props.handleCloseQuestionnaireModal}
        handleAcivateCamera={props.handleAcivateCamera}
        _handleFail={props.onFail}
        handlePhotoCaptured={props.handleTakeFromGallery}
        currentConditionIndex={props.currentConditionIndex}
      />

      <WarningModal
        isVisible={props.showWarningModal}
        language={props.language}
        title={props.modalWarningConfig.title}
        subtitle={props.modalWarningConfig.subtitle}
        options={props.modalWarningConfig.options}
        profileColor={props.profileColor}
        onCloseModalWarning={props.onCloseModalWarning}
        currentConditionIndex={props.currentConditionIndex}
        selectNewFile={props.selectNewFile}
        translation={props.translation}
      />

      <QuestionnaireUploadCVModal isVisible={props.isUploadingCV} language={props.language} />
      {/* <UploadReportStatus isFixed={true} fromHome={false} /> */}
      <View style={styles.questionnaireRoot}>
        <View style={styles.questionnaireContainer}>
          <TouchableWithoutFeedback>
            <View style={styles.questionnaireNav}>
              <QuestionnaireProgress
                completed={props.completedCount}
                total={props.selectedReport.questions.length}
                language={props.language}
                isTutorialMode={props.isTutorialMode}
                isShowQuestionTutorialModal={props.isShowQuestionTutorialModal}
              />
            </View>
          </TouchableWithoutFeedback>
          <QuestionnaireSwiper
            handleCloseCamera={props.handleCloseCamera}
            allowRecommendation={props.organization?.allowRecommendations}
            // TODO: remove `organization.features.video` when video and photo is splitted
            hasVideoAttachment={props.organization?.features?.video}
            editingPhotoIndex={props.editingPhotoIndex}
            questions={props.selectedReport.questions}
            tutorialQuestionType={props.tutorialQuestionType}
            onQuestionIndexChanged={() => props.saveQuestionnaireDebounced()}
            onNavReference={props.handleNavReference}
            onNavRecordVideo={props.handleNavRecordVideo}
            onNavPlaybackVideo={props.handleNavPlaybackVideo}
            onNavCreateCompany={props.handleNavCreateCompany}
            onSelectWeightedChoice={(index: number, text: string, condition?: string | number) => {
              return props.handleInput('weighted-choice', index, text, condition!);
            }}
            onSelectChoice={(index: number, text: string, condition?: string | number) =>
              props.handleInput('choice', index, text, condition!)
            }
            onSelectChecklist={(index: number, text: string, condition?: string | number) =>
              props.handleInput('checklist', index, text, condition!)
            }
            onInputAnswer={(index: number, text: string, condition?: string | number) =>
              props.handleInput('answer', index, text, condition!)
            }
            onInputComment={(index: number, text: string, condition?: string | number) =>
              props.handleInput('comment', index, text, condition!)
            }
            onInputNumber={(index: number, text: string, condition?: string | number) =>
              props.handleInput('number', index, text, condition!)
            }
            onInputScore={(index: number, score: number, condition?: string | number) =>
              props.handleInput('score', index, score, condition!)
            }
            onInputDecimal={(index: number, text: string, condition?: string | number) =>
              props.handleInput('range-flag', index, text, condition!)
            }
            onInputCompetitorAnalysis={(index: number, text: string, type: any) =>
              props.handleInput('competitor', index, text, '', type)
            }
            onConfirmEditPhoto={props.handleConfirmEditPhoto}
            onActivateImagePicker={props.handleActivateImagePicker}
            handleUploadDocuments={props.handleUploadDocuments}
            handleEditUploadFile={props.handleEditUploadFile}
            showCamera={props.showCamera}
            handleTakePhoto={props.handleTakePhoto}
            onSetRemedies={props.handleSetRemedies}
            isTutorialMode={props.isTutorialMode}
            isShowTooltipNA={props.isShowTooltipNA}
            isShowQuestionTutorialModal={props.isShowQuestionTutorialModal}
            handleShowTooltipNA={props.handleShowTooltipNA}
            handleShowTutorialQuestionModal={props.handleShowTutorialQuestionModal}
            handleChangeTutorialQuestionType={props.handleChangeTutorialQuestionType}
            handleDismissTutorialQuestionModal={props.handleDismissTutorialQuestionModal}
            handleDismissTutorialMode={props.handleDismissTutorialMode}
          />

          {props.isTutorialMode ? (
            <View style={styles.helpButton}>
              <TouchableOpacity onPress={() => props.handleShowTutorialQuestionModal(true)}>
                <Image
                  source={require('../../assets/icons/question-icon.png')}
                  style={{ width: 30, resizeMode: 'contain' }}
                />
              </TouchableOpacity>
            </View>
          ) : null}
          <View style={[styles.buttonContainer, props.hasNotch ? styles.buttonContainerNotch : null]}>
            {props.isBusy ? (
              <ActivityIndicator size="small" color="#3cd070" />
            ) : (
              <View
                style={{
                  flexDirection: 'row',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-around',
                  margin: 'auto',
                  // paddingBottom: SCREEN_WIDTH >= 768 ? 20 : 10
                }}
              >
                <View>
                  <Button
                    testID={'clear-btn'}
                    style={styles.buttonClear}
                    color={props.profileColor}
                    disabled={props.isBusy}
                    onPress={props.handleConfirmClear}
                    title={t('common:clear')}
                  />
                </View>
                <View>
                  <Button
                    testID={'prev-btn'}
                    style={[styles.buttonPrevOutlined, { borderColor: props.profileColor }]}
                    color={props.profileColor}
                    disabled={props.currentQuestionIndex === 0 ? true : false}
                    onPress={props.handleMoveToPrevQuestion}
                    title={t('questionnaire:container.backQuestion')}
                  />
                </View>
                <View>
                  {showReviewButton ? (
                    <Button
                      testID={'finish-btn'}
                      style={styles.buttonNextPill}
                      disabled={props.isBusy}
                      backgroundColor={props.profileColor}
                      onPress={
                        !props.isMultiSite ? props.handleSubmitQuestionnaire : () => props.handleFinishSection(false)
                      }
                      title={
                        props.isBusy
                          ? t('questionnaire:tutorial.loadingCapital')
                          : props.isMultiSite
                          ? t('questionnaire:tutorial.finishSection')
                          : t('questionnaire:tutorial.reviewReport')
                      }
                    />
                  ) : (
                    <Button
                      testID={'next-btn'}
                      style={styles.buttonNextPill}
                      backgroundColor={props.profileColor}
                      onPress={props.handleMoveToNextQuestion}
                      title={t('questionnaire:container.nextQuestion')}
                      disabled={!(props.currentQuestionIndex < props.selectedReport.questions.length - 1)}
                    />
                  )}
                </View>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

export default QuestionnaireContainer;

const styles = StyleSheet.create({
  rootQusionareContainer: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#fff',
    // `height` is applied inline (viewport - header). overflow:hidden keeps the body
    // within that bound so the inner question ScrollView scrolls instead of the page.
    overflow: 'hidden',
  },
  helpButton: {
    position: 'absolute',
    bottom: 90,
    right: 20
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  questionnaireRoot: {
    flex: 1,
    flexDirection: 'column',
  },
  questionnaireContainer: {
    flex: 1,
    zIndex: 1
  },
  headerText: {
    textAlign: 'center',
    fontSize: SCREEN_WIDTH <= 320 ? 14 : 18,
    fontWeight: 'normal',
    marginTop: 25,
    paddingHorizontal: 30
  },
  questionnaireNav: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 100,
    marginHorizontal: 'auto',
    paddingTop: 20,
    paddingHorizontal: 5,
    paddingBottom: 15
  },
  buttonContainer: {
    // Pinned to the bottom of the (viewport-bounded) questionnaire so it is always
    // visible; the question ScrollView reserves matching paddingBottom to clear it.
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: 64,
    height: SCREEN_HEIGHT * 0.01,
    backgroundColor: '#fff',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E5E7EB',
    justifyContent: 'center',
    zIndex: 2,
  },
  buttonContainerNotch: {
    height: SCREEN_HEIGHT * 0.1 + 12
  },
  buttonContainerHidden: {
    minHeight: 64,
    height: SCREEN_HEIGHT * 0.1,
    paddingVertical: 12,
    paddingHorizontal: SCREEN_WIDTH * 0.2,
    backgroundColor: '#535353',
    zIndex: -100
  },
  buttonNext: {
    width: 120
    // justifyContent: 'flex-start'
    // marginHorizontal: 'auto'
  },
  buttonPrev: {
    width: 120
    // justifyContent: 'flex-end'
    // marginHorizontal: 'auto'
  },
  button: {
    width: 300,
    marginHorizontal: 'auto'
  },
  // Footer pill buttons (audit-lite-style bar): Clear (text) | Previous (outlined) | Next/Review (filled)
  buttonClear: {
    backgroundColor: 'transparent',
    minHeight: undefined,
    shadowOpacity: 0,
    elevation: 0,
    paddingHorizontal: 8
  },
  buttonPrevOutlined: {
    width: 120,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderRadius: 28,
    shadowOpacity: 0,
    elevation: 0
  },
  buttonNextPill: {
    width: 120,
    borderRadius: 28
  }
});
